import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EmptyModulesState } from '@features/Dashboard';
import { cn } from '@shared/lib';
import { ChevronRightIcon, LibraryIcon, PlayIcon, PlusIcon } from '@shared/ui';
import type { ModuleSummary } from '@shared/types';
import { lineStatus, trainPath, useTrainingPlans } from './moduleTraining';

interface ModulesShelfProps {
  modules: ModuleSummary[];
  loading: boolean;
  /** Сколько добавленных сегодня и не тренированных карточек в модуле (id → число): такой модуль — первым и с меткой
   *  «+N». */
  fresh: Record<string, number>;
  /** Выбранный модуль: ниже — только его слова (владелец 2026-10-28); нажатие на выбранный снимает выбор. */
  selectedId: string | null;
  onSelect: (module: ModuleSummary) => void;
  onCreateModule: () => void;
  onOpenAll: () => void;
  onOpenReady: () => void;
  onOpenDeleted: () => void;
}

/**
 * Модули на телефоне — полкой со свайпом (вариант B, 2026-10-28): карточки модулей с «Учить», над полкой — плашка
 * «Готовые наборы | Новый модуль». Нажатие на карточку выбирает модуль — ниже его слова, как на компьютере; страница
 * модуля — по «Открыть модуль ›» над словами. Полка низкая, поэтому «Слова» с только что созданным словом начинаются
 * на первом экране. Полный список с поиском и удалёнными — по «Все ›».
 */
export function ModulesShelf({
  modules,
  loading,
  fresh,
  selectedId,
  onSelect,
  onCreateModule,
  onOpenAll,
  onOpenReady,
  onOpenDeleted,
}: ModulesShelfProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plans = useTrainingPlans();
  // Модуль, куда сегодня легли слова, — первым: видно, куда легло.
  const ordered = useMemo(
    () => [...modules].sort((a, b) => (fresh[b.id] ? 1 : 0) - (fresh[a.id] ? 1 : 0)),
    [modules, fresh],
  );

  if (!loading && modules.length === 0) {
    return <EmptyModulesState onCreateModule={onCreateModule} onOpenDeleted={onOpenDeleted} emailInDock />;
  }

  const tile = 'relative h-[140px] w-[156px] flex-none snap-start rounded-[18px] border border-solid';
  return (
    <section aria-labelledby="words-modules-title">
      <div className="flex min-h-[44px] items-center justify-between gap-3">
        <h2 id="words-modules-title" className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted">
          {t('words.shelf.title')}
          {modules.length > 0 && <span className="tabular-nums">{` · ${modules.length}`}</span>}
        </h2>
        <button
          type="button"
          onClick={onOpenAll}
          className="-mr-2 inline-flex min-h-[44px] items-center gap-0.5 rounded-pill px-2 text-[14px] font-semibold text-accent-hi outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {t('words.shelf.all')}
          <ChevronRightIcon aria-hidden className="h-4 w-4" />
        </button>
      </div>
      {/* «Готовые наборы» и «Новый модуль» — плашкой над полкой, а не карточками в её конце: до конца ленты долистывают
          не все (владелец 2026-10-28). */}
      <div className="mt-1 flex h-11 items-stretch overflow-hidden rounded-[14px] border border-solid border-line bg-card">
        <button
          type="button"
          onClick={onOpenReady}
          className="flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap px-3 text-[13px] font-semibold text-fg outline-none transition-colors active:bg-elev focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
        >
          <LibraryIcon aria-hidden className="h-4 w-4 flex-none text-muted max-[359px]:hidden" />
          {t('words.shelf.ready')}
        </button>
        <span aria-hidden className="my-2 w-px flex-none bg-line" />
        <button
          type="button"
          onClick={onCreateModule}
          className="flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap px-3 text-[13px] font-semibold text-fg outline-none transition-colors active:bg-elev focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
        >
          <PlusIcon aria-hidden className="h-4 w-4 flex-none text-muted max-[359px]:hidden" />
          {t('words.shelf.newModule')}
        </button>
      </div>
      {/* Лента до краёв экрана: поле страницы — отступом внутри, первая карточка не прижата к краю. */}
      <div className="-mx-4 mt-2.5 flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-4 pb-1 [scroll-padding-inline:16px] [scrollbar-width:none] sm:-mx-6 sm:px-6 sm:[scroll-padding-inline:24px] [&::-webkit-scrollbar]:hidden">
        {loading
          ? Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className={cn(tile, 'animate-skeleton border-line bg-card')} />
            ))
          : ordered.map((m) => {
              const plan = plans.get(m.id);
              const status = plan ? lineStatus(plan, t) : null;
              const added = fresh[m.id] ?? 0;
              const selected = m.id === selectedId;
              const canTrain = !!plan && plan.ready > 0;
              return (
                <div
                  key={m.id}
                  className={cn(
                    tile,
                    selected
                      ? 'border-accent/60 bg-[linear-gradient(160deg,rgba(243,106,28,0.14),rgba(243,106,28,0)_65%)] bg-card'
                      : 'border-line bg-card',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(m)}
                    aria-pressed={selected}
                    aria-label={t('words.mine.select', { name: m.title })}
                    className={cn(
                      'flex h-full w-full flex-col rounded-[18px] p-3 pl-3.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                      canTrain && 'pb-[52px]',
                    )}
                  >
                    <span className={cn('line-clamp-2 text-[15px] font-medium leading-[19px] text-fg', added > 0 && 'pr-9')}>
                      {m.title}
                    </span>
                    <span className="mt-0.5 text-[12px] leading-4 text-muted">{t('training.cardCount', { count: m.words })}</span>
                    {added > 0 ? (
                      <span className="mt-auto truncate text-[12px] font-semibold leading-[15px] text-accent-hi">
                        {t('words.mine.fresh', { count: added })}
                      </span>
                    ) : (
                      status && (
                        <span
                          className={cn(
                            'mt-auto truncate text-[12px] leading-[15px]',
                            status.repeat ? 'text-accent-hi' : 'text-muted',
                          )}
                        >
                          {status.text}
                        </span>
                      )
                    )}
                  </button>
                  {added > 0 && (
                    <span className="pointer-events-none absolute right-2.5 top-2.5 inline-flex h-5 items-center rounded-pill bg-accent px-2 text-[11px] font-bold text-fg-inv">
                      +{added}
                    </span>
                  )}
                  {canTrain && (
                    <button
                      type="button"
                      onClick={() => navigate(trainPath(plan, 'shelf'), { state: { fromWords: true } })}
                      aria-label={t('words.mine.train', { name: m.title })}
                      title={t('words.mine.trainTitle')}
                      className={cn(
                        'absolute bottom-2.5 left-3 inline-flex h-9 items-center gap-1.5 rounded-pill px-3.5 text-[13px] font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent',
                        selected || added > 0 ? 'bg-accent text-fg-inv' : 'bg-elev text-fg hover:bg-accent hover:text-fg-inv',
                      )}
                    >
                      <PlayIcon className="h-3 w-3" />
                      {t('words.mine.learn')}
                    </button>
                  )}
                </div>
              );
            })}
      </div>
    </section>
  );
}
